import type { JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

type ShowToast = (text: string) => void;

const ToastContext = createContext<ShowToast>(() => undefined);

const SHOWN_MS = 2400;

/**
 * One line of confirmation at the bottom centre — "Signed in as …",
 * "Agent settings saved". A new toast replaces the one on screen.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback<ShowToast>((next) => {
    clearTimeout(timer.current);
    setText(next);
    timer.current = setTimeout(() => setText(''), SHOWN_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {text !== '' && (
        <div
          role="status"
          className="bg-ink text-bg shadow-panel animate-[rise_.25s_ease_both] fixed bottom-[calc(96px+env(safe-area-inset-bottom))] left-1/2 tab:bottom-6 z-[60] flex -translate-x-1/2 items-center gap-2.5 rounded-[10px] px-4 py-2.5 text-[13px] font-medium"
        >
          <span className="bg-acc size-[7px] rounded-full" />
          {text}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  return useContext(ToastContext);
}
